import { useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import {
  AutomationConfigApiService,
  type AutomationDocSummary,
} from '../services/automation-config-api';

/** Summaries of one doc, keyed by automation block ID. */
export type AutomationDocSummaryMap = ReadonlyMap<string, AutomationDocSummary>;

export interface AutomationSummaryDocRequest {
  docId: string;
  /** Automation blocks currently known in the doc; a change reloads it. */
  blockIds: readonly string[];
}

/**
 * Loads trigger, schedule state and last run for the given docs (the
 * expanded groups of the sidebar automation overview). A doc reloads when
 * it is requested again after its block set changed. Docs whose request
 * failed stay absent, so the overview never shows a guessed status.
 */
export function useAutomationDocSummaries(
  workspaceId: string,
  docs: readonly AutomationSummaryDocRequest[]
): ReadonlyMap<string, AutomationDocSummaryMap> {
  const configApi = useService(AutomationConfigApiService);
  const [summaries, setSummaries] = useState<
    ReadonlyMap<string, AutomationDocSummaryMap>
  >(new Map());

  const requestKey = useMemo(
    () =>
      JSON.stringify(
        docs.map(doc => [doc.docId, [...doc.blockIds].sort()] as const)
      ),
    [docs]
  );

  useEffect(() => {
    const requested = JSON.parse(requestKey) as [string, string[]][];
    let cancelled = false;
    for (const [docId] of requested) {
      configApi
        .getDocSummaries(workspaceId, docId)
        .then(list => {
          if (cancelled) return;
          setSummaries(previous => {
            const next = new Map(previous);
            next.set(
              docId,
              new Map(list.map(summary => [summary.blockId, summary]))
            );
            return next;
          });
        })
        .catch(error => {
          console.error('[AutomationOverview] summaries failed', docId, error);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [configApi, requestKey, workspaceId]);

  return summaries;
}
